/** Archive a contract that is still under review — admin only.
 *
 * A soft delete, and the copy says so plainly: nothing is destroyed, the audit
 * trail survives, and an admin can find the contract again with the Archived
 * status filter. Calling it "Delete" would misdescribe what happens and invite
 * the reviewer to expect erasure they are not getting.
 *
 * Permanent erasure stays in Settings → danger zone, where it is scoped to a
 * whole client and gated accordingly.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { toast } from 'sonner';

interface Props {
  contractId: string;
  contractTitle: string;
  /** Contract status — the button only shows pre-activation. */
  status: string;
}

const ARCHIVABLE = new Set(['draft', 'under_review']);

export function ArchiveContractButton({ contractId, contractTitle, status }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // An active contract is ended by terminating it, not archiving it — the
  // server refuses with 409, so don't offer the action in the first place.
  if (!ARCHIVABLE.has(status)) return null;

  async function handleArchive() {
    setSubmitting(true);
    try {
      await api.post(`/api/v1/contracts/${contractId}/archive`, {
        reason: reason.trim() || null,
      });
      toast.success(`"${contractTitle}" archived.`);
      setOpen(false);
      navigate('/contracts');
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError ? err.message : 'Could not archive this contract.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 text-destructive hover:text-destructive"
        onClick={() => { setOpen(true); }}
      >
        <Archive className="h-4 w-4" aria-hidden="true" />
        Archive contract
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        {/* No manual role / aria-labelledby / aria-describedby here: Radix
            wires alertdialog + both associations from Title and Description,
            and overriding them detaches the label it just set. */}
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive &ldquo;{contractTitle}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                It leaves the contracts list and the review queue, and any
                analysis still running is cancelled.
              </span>
              <span className="block">
                Nothing is deleted. The contract, its agent results and its audit
                trail are kept, and you can find it again by filtering the
                contracts list by <strong>Archived</strong>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-1.5">
            <label htmlFor="archive-reason" className="text-sm font-medium">
              Reason <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="archive-reason"
              value={reason}
              onChange={(e) => { setReason(e.target.value); }}
              placeholder="e.g. duplicate upload, wrong document"
              maxLength={500}
              rows={2}
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-violet-400 resize-y"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={submitting}
              onClick={() => { void handleArchive(); }}
              className="gap-1.5"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Archive contract
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
